// Dividend rules: shares held on the ex date, duplicates, booking a payment, upcoming dividends,
// the past-dividend backfill and ticker corrections. Ported line for line from v1's sharesOnEx(),
// isDup(), bookEvent(), settleUpcoming(), importDividends(), whtFor(), findPastDividends() and
// renameSym(). They change the state they're given, as v1's did.
/* eslint-disable @typescript-eslint/no-explicit-any */
import { addDaysIso, days, isoOf, now, today } from './clock';
import { N, isSgx, okC, type State } from './state';
import { compute, type Position } from './positions';

const qtyF = (n: number) => new Intl.NumberFormat(undefined, { maximumFractionDigits: 8 }).format(n);

/** Shares held before the ex date: buys minus sells dated strictly before it. */
export function sharesOnEx(S: State, sym: string, ex: string): number {
  let q = 0; for (const t of S.txns) if (t.type === 'stock' && t.sym === sym && t.date < ex) q += t.side === 'buy' ? N(t.qty) : -N(t.qty);
  return Math.max(0, +q.toFixed(8));
}
export const evId = (e: { sym?: unknown; ex?: unknown }) => 'feed-' + e.sym + '-' + e.ex;

export function isDup(S: State, e: any): boolean {
  if (S.dismissed.includes(evId(e))) return true;
  return S.income.some((i) => i.id === evId(e) || (i.kind === 'dividend' && i.sym === e.sym && Math.abs(days(i.date, e.pay || e.ex)) <= 12));
}

export function bookEvent(S: State, e: any): 'notheld' | 'dup' | 'added' {
  const q = sharesOnEx(S, e.sym, e.ex); if (!(q > 0)) return 'notheld';
  if (isDup(S, e)) return 'dup';
  const g = +(q * N(e.dps)).toFixed(2), wht = Math.min(1, Math.max(0, N(e.wht)));
  S.income.push({
    id: evId(e), type: 'stock', kind: 'dividend', sym: e.sym, date: e.pay || e.ex, gross: g, tax: +(g * wht).toFixed(2), ccy: okC(e.ccy) ? e.ccy : 'SGD',
    note: [e.note, `${qtyF(q)} shares × ${e.dps} ${e.ccy}`, wht ? `${Math.round(wht * 100)}% withheld` : ''].filter(Boolean).join('; '),
  });
  return 'added';
}

/** Books announced dividends once their payment date has passed. Returns how many were added. */
export function settleUpcoming(S: State): number {
  const t = today(), keep: any[] = []; let n = 0;
  for (const e of S.upcoming) { if ((e.pay || e.ex) <= t) { if (bookEvent(S, e) === 'added') n++; } else keep.push(e); }
  S.upcoming = keep; return n;
}

export interface ImportResult { added: number; upcoming: number; dup: number; notheld: number; redated?: number }
export function importDividends(S: State, d: any): ImportResult {
  const r: any = { added: 0, upcoming: 0, dup: 0, notheld: 0 }, t = today();
  const redated = redateEstimated(S, d.events || []); if (redated) r.redated = redated;
  for (const raw of d.events || []) {
    const e = { sym: String(raw.sym || '').toUpperCase().replace(/\.SI$/, '').replace(/^SGX:/, ''), ex: raw.ex, pay: raw.pay || '', dps: N(raw.dps), ccy: okC(raw.ccy) ? raw.ccy : 'SGD', wht: N(raw.wht), note: raw.note || '' };
    if (!e.sym || !/^\d{4}-\d{2}-\d{2}$/.test(e.ex || '') || !(e.dps > 0)) continue;
    if ((e.pay || e.ex) > t) {
      if (isDup(S, e)) { r.dup++; continue; }
      const was = S.upcoming.find((u) => evId(u) === evId(e)); if (was && was.dps === e.dps && (was.pay || '') === (e.pay || '')) { r.dup++; continue; }
      const held = e.ex <= t ? sharesOnEx(S, e.sym, e.ex) : sharesOnEx(S, e.sym, '9999-12-31');
      if (!(held > 0)) { r.notheld++; continue; }
      S.upcoming = S.upcoming.filter((u) => evId(u) !== evId(e)); S.upcoming.push(e); r.upcoming++;
    } else r[bookEvent(S, e)]++;
  }
  return r;
}

const ESTIMATED = 'Yahoo Finance; pay date estimated (3 weeks after ex date)';
/** v2 (13 Oct 2026): US dividends recorded automatically with an estimated pay date (3 weeks after the
 *  ex date) move to the actual pay date once the dividend check finds it. Only entries still as recorded
 *  (a feed id, the estimated date for that id's ex date, the estimate note) are changed; anything edited by
 *  hand is left. The feed's ex date may differ from the recorded one by up to 3 days (Yahoo sometimes
 *  revises an ex date by a day). */
export function redateEstimated(S: State, events: any[]): number {
  const bySym: Record<string, any[]> = {};
  for (const raw of events || []) {
    if (!raw || !/^\d{4}-\d{2}-\d{2}$/.test(raw.pay || '') || !/^\d{4}-\d{2}-\d{2}$/.test(raw.ex || '') || String(raw.note || '').startsWith(ESTIMATED)) continue;
    const sym = String(raw.sym || '').toUpperCase().replace(/\.SI$/, '').replace(/^SGX:/, '');
    (bySym[sym] = bySym[sym] || []).push(raw);
  }
  let n = 0;
  for (const i of S.income) {
    if (i.kind !== 'dividend' || !bySym[i.sym] || !String(i.note || '').startsWith(ESTIMATED)) continue;
    const m = String(i.id || '').match(/^feed-(.+)-(\d{4}-\d{2}-\d{2})$/);
    if (!m || m[1] !== i.sym || i.date !== addDaysIso(m[2], 21)) continue;
    let best: any = null;
    for (const e of bySym[i.sym]) { const gap = Math.abs(days(e.ex, m[2])); if (gap <= 3 && (!best || gap < Math.abs(days(best.ex, m[2])))) best = e; }
    if (!best || best.pay === i.date) continue;
    i.date = best.pay; i.note = String(best.note || '') + String(i.note).slice(ESTIMATED.length); n++;
  }
  return n;
}

/** Withholding tax for a stock: from the latest dividend recorded for it, else 0 for SGX and 30% otherwise. */
export function whtFor(S: State, p: { key: string; sym: string }): number {
  const d = S.income.filter((i) => i.kind === 'dividend' && i.sym === p.sym && N(i.gross) > 0).sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0)).pop();
  return d ? Math.round(N(d.tax) / N(d.gross) * 100) / 100 : isSgx(S, p) ? 0 : 0.3;
}

/** Find past dividends, step 1: the stocks to look up (first bought more than 110 days ago). */
export function pastDividendList(S: State, syms: string[] | null, ps: Position[] = compute(S)): { sym: string; since: string; wht: number }[] {
  const cut = isoOf(now() - 110 * 864e5), list: { sym: string; since: string; wht: number }[] = [];
  for (const p of ps) {
    if (p.type !== 'stock' || (syms && !syms.includes(p.sym))) continue;
    const first = S.txns.filter((t) => t.type === 'stock' && t.sym === p.sym && t.side === 'buy').map((t) => t.date).sort()[0];
    if (first && first < cut) list.push({ sym: isSgx(S, p) ? p.sym + '.SI' : p.sym, since: first, wht: whtFor(S, p) });
  }
  return list;
}
/** Find past dividends, step 2: of the events found, the ones not already recorded. Each recorded
 *  dividend covers the latest ex date between 75 days before it and 3 days after it. */
export function uncoveredEvents(S: State, events: any[]): any[] {
  const evs: any[] = [], bySym: Record<string, any[]> = {};
  for (const raw of events || []) { const sym = String(raw.sym || '').toUpperCase().replace(/\.SI$/, ''); (bySym[sym] = bySym[sym] || []).push(raw); }
  for (const sym in bySym) {
    const es = bySym[sym].filter((e) => /^\d{4}-\d{2}-\d{2}$/.test(e.ex || '')).sort((a, b) => (a.ex < b.ex ? -1 : 1)), covered = new Set<string>();
    for (const i of S.income) {
      if (i.kind !== 'dividend' || i.sym !== sym) continue;
      let hit: any = null; for (const e of es) if (e.ex <= addDaysIso(i.date, 3) && e.ex >= addDaysIso(i.date, -75)) hit = e;
      if (hit) covered.add(hit.ex);
    }
    for (const e of es) if (!covered.has(e.ex)) evs.push(e);
  }
  return evs;
}

/** When a ticker is corrected on its only trades, the payments recorded under the old ticker move with it. */
export function renameSym(S: State, type: string, from: string, to: string): number {
  let n = 0; const fid = (id: any) => (String(id).startsWith('feed-' + from + '-') ? 'feed-' + to + String(id).slice(5 + from.length) : id);
  for (const i of S.income) if (i.type === type && i.sym === from) { i.sym = to; i.id = fid(i.id); n++; }
  if (type === 'stock') { for (const e of S.upcoming) if (e.sym === from) e.sym = to; S.dismissed = S.dismissed.map(fid); }
  if (type === 'crypto') for (const st of S.stakes) if (st.sym === from) st.sym = to;
  return n;
}
